import { Button, CircularProgress } from '@mui/material';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { theme } from '@/modules/common/theme';
import { assertNever } from '@/helpers/utils';
import { StyledTooltip } from '@/helpers/styles';
import { FpsServiceFactory } from '@/modules/floorplanV2/service/FpsServiceFactory';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { useConfirm } from '@/modules/common/components/useConfirm';

import { useFpsStore } from '../store/useFpsStore';
import { Status, Step } from '../types';
import { ExportConfigPanel } from './ExportConfigPanel';

const ValidationPanelComponent = () => {
  const { t } = useTranslation(['interface']);
  const { status, step } = useFpsStore();
  const { Dialog: ConfirmDialog, confirm } = useConfirm();
  const fpsSvc = useMemo(() => FpsServiceFactory.getInstance().createFpsService(), []);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const anchorRef = useRef(null);
  const loading = status === Status.WaitingForResponse;
  const [exportConfigPanelOpen, setExportConfigPanelOpen] = useState(false);
  
  const handlePanelClick = useCallback(async () => {
    if (status === Status.WaitingForResponse) return;

    // If the user has selected to export the proximity matrix or travel duration cost matrix, we need to confirm with them before proceeding with the export. Including them may greatly increase the time of the export
    const generateProximityMatrix = fddmSvc.getPreference('exportSettings.generateProximityMatrix');
    const generateTravelDurationCostMatrix = fddmSvc.getPreference('exportSettings.generateTravelDurationCostMatrix');
    if (step === Step.Export && (generateProximityMatrix || generateTravelDurationCostMatrix)) {
      const answer = await confirm({
        title: t('interface:export.confirm_panel.title'),
        message: t('interface:export.confirm_panel.message'),
      });

      if (!answer) {
        setExportConfigPanelOpen(true);
        return;
      }
    }

    switch (step) {
      case Step.Export:
        fpsSvc.exportToRuntimeStack(
          fddmSvc.getPreference('exportSettings.generateTracks'),
          generateProximityMatrix,
          generateTravelDurationCostMatrix,
        );
        break;
      case Step.Tracks:
        fpsSvc.generateTracks();
        break;
      case Step.Validate:
        fpsSvc.validateFloorplan();

        break;
      default:
        assertNever(step);
    }
  }, [confirm, fddmSvc, fpsSvc, status, step, t]);

  const label = useMemo(() => {
    if (status === Status.WaitingForResponse) return ' ';

    switch (step) {
      case Step.Export:
        return t('interface:validation.label.export');
      case Step.Tracks:
        return t('interface:validation.label.generate_tracks');
      case Step.Validate:
        return t('validation.label.validate_floorplan');
      default:
        assertNever(step);
    }
  }, [status, step, t]);

  const tooltip = useMemo(() => {
    switch (step) {
      case Step.Export:
        if (status === Status.Idle) return t('interface:validation.tooltip.export');
        return t('interface:validation.tooltip.export_ongoing');
      case Step.Tracks:
        if (status === Status.Idle) return t('interface:validation.tooltip.generate_tracks');
        return t('interface:validation.tooltip.generate_tracks_ongoing');
      case Step.Validate:
        if (status === Status.Idle) return t('interface:validation.tooltip.validate_floorplan');
        return t('interface:validation.tooltip.validate_floorplan_ongoing');
      default:
        assertNever(step);
    }
  }, [step, status, t]);

  return (
    <>
      {step === Step.Export && <ExportConfigPanel open={exportConfigPanelOpen} setOpen={setExportConfigPanelOpen} />}
      <StyledTooltip title={tooltip} placement='bottom'>
        <Button
          ref={anchorRef}
          aria-label='Validation'
          variant='text'
          onClick={handlePanelClick}
          startIcon={loading && <CircularProgress size={16} />}
          sx={{
            width: '200px',
            border: 0,
            color: theme.palette.neutral.dark,
            '&:hover': {
              backgroundColor: theme.palette.shades.light,
              color: theme.palette.neutral.darker,
            },
            '& .MuiButton-startIcon': { margin: 0, padding: 0 },
          }}
        >
          {label}
        </Button>
      </StyledTooltip>
      <ConfirmDialog negativeBtnTextCustom='no' stopPropagation />
    </>
  );
};

export const ValidationPanel = memo(ValidationPanelComponent);
